import { H1, H2, HeaderTextBlock } from "@/components/Typography";
import { useGetEmailDashboard } from "@/requests/hooks/collect";
import parseQueryString from "@/utils/parseQueryString";
import { useTranslation } from "react-i18next";
import { getDashboardDateRanges } from "@/utils/dateManager";
import { useSearchParams } from "react-router-dom";
import { useState } from "react";
import { Select } from "@/components/Select";
import {
  faArrowRight,
  faCalendarRange,
  faExclamationTriangle,
} from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import TopLevelCards from "@/components/EmailDashboard/TopLevelCards";
import McrSummaryTable from "@/components/EmailDashboard/McrSummaryTable";
import MonthlyBreakdownTable from "@/components/EmailDashboard/MonthlyBreakdownTable";
import ChartGrid from "@/components/EmailDashboard/ChartGrid";
import { Badge } from "@/components/Badge";
import EmailDashboardLoading from "@/routes/Email/Dashboard/EmailDashboardLoading";

import abTestImage from "@/assets/svgs/ab-test-experiment.svg";

interface ITimePeriod {
  label: string;
  value: string;
  startDate: string | undefined;
  endDate: string | undefined;
}

const Dashboard = () => {
  const { t } = useTranslation();

  const {
    thisMonth,
    previousMonth,
    previousThreeMonths,
    previousSixMonths,
    previousTwelveMonths,
  } = getDashboardDateRanges();

  const timePeriodOptions: ITimePeriod[] = [
    {
      label: t("email.dashboard.timePeriod.allTime"),
      value: "",
      startDate: undefined,
      endDate: undefined,
    },
    {
      label: t("email.dashboard.timePeriod.thisMonth"),
      value: `${thisMonth.startDate}&${thisMonth.endDate}`,
      startDate: thisMonth.startDate,
      endDate: thisMonth.endDate,
    },
    {
      label: t("email.dashboard.timePeriod.previousMonth"),
      value: `${previousMonth.startDate}&${previousMonth.endDate}`,
      startDate: previousMonth.startDate,
      endDate: previousMonth.endDate,
    },
    {
      label: t("email.dashboard.timePeriod.previousThreeMonths"),
      value: `${previousThreeMonths.startDate}&${previousThreeMonths.endDate}`,
      startDate: previousThreeMonths.startDate,
      endDate: previousThreeMonths.endDate,
    },
    {
      label: t("email.dashboard.timePeriod.previousSixMonths"),
      value: `${previousSixMonths.startDate}&${previousSixMonths.endDate}`,
      startDate: previousSixMonths.startDate,
      endDate: previousSixMonths.endDate,
    },
    {
      label: t("email.dashboard.timePeriod.previousTwelveMonths"),
      value: `${previousTwelveMonths.startDate}&${previousTwelveMonths.endDate}`,
      startDate: previousTwelveMonths.startDate,
      endDate: previousTwelveMonths.endDate,
    },
  ];

  const payload = parseQueryString();
  const [searchParams, setSearchParams] = useSearchParams({});

  const getActiveTimePeriod = (): ITimePeriod => {
    if (searchParams.get("startDate") && searchParams.get("endDate")) {
      return (
        timePeriodOptions.find(
          ({ startDate }) => startDate === searchParams.get("startDate"),
        ) || timePeriodOptions[0]
      );
    }

    return timePeriodOptions[0];
  };

  const [selectedTimePeriod, setSelectedTimePeriod] = useState<ITimePeriod>(
    getActiveTimePeriod(),
  );

  const {
    data: dashboardData,
    isLoading: isDashboardLoading,
    isError: isDashboardError,
  } = useGetEmailDashboard(payload);

  const handleChangeTimePeriod = (e: unknown) => {
    const typedEvent = e as ITimePeriod;

    setSelectedTimePeriod(typedEvent);
    if (typedEvent.startDate && typedEvent.endDate) {
      setSearchParams({
        startDate: typedEvent.startDate,
        endDate: typedEvent.endDate,
      });

      return;
    }

    setSearchParams({});
  };

  const renderAbTestMessage = () => (
    <div
      data-testid="ab-test-active-message"
      className="mx-auto flex max-w-[850px] flex-col gap-50">
      <HeaderTextBlock
        align="center"
        featureText={t("email.dashboard.abTest.header.feature")}
        featureTextColor="primary"
        customHtml={
          <div className="mt-10">
            <H1 className="font-bold text-secondary">
              {t("email.dashboard.abTest.header.title")}
            </H1>
          </div>
        }
        customHtmlPosition="middle"
      />
      <img src={abTestImage} className="mx-auto w-1/2" />
    </div>
  );

  const getDisplayableDateRange = () => {
    if (selectedTimePeriod?.label === "All time") return null;

    return (
      <div className="flex flex-row items-center gap-10">
        <Badge icon={faCalendarRange} variant="info" textSize="base">
          {selectedTimePeriod?.startDate}{" "}
          <FontAwesomeIcon icon={faArrowRight} /> {selectedTimePeriod?.endDate}
        </Badge>
      </div>
    );
  };

  const renderDashboard = () => {
    if (isDashboardLoading) return <EmailDashboardLoading />;

    return (
      <>
        <TopLevelCards data={dashboardData!} />
        <McrSummaryTable data={dashboardData!} />
        <ChartGrid data={dashboardData!} />
        <MonthlyBreakdownTable data={dashboardData!} />
      </>
    );
  };

  if (!!dashboardData?.isAbTestActive) return renderAbTestMessage();

  if (!isDashboardLoading && isDashboardError) {
    return (
      <div
        data-testid="email-dashboard-error"
        className="mt-50 flex flex-col items-center justify-center gap-20">
        <FontAwesomeIcon
          icon={faExclamationTriangle}
          className="text-4xl text-red"
        />
        <div className="max-w-[400px] text-center">
          <H2 className="font-bold">{t("email.dashboard.error")}</H2>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="email-dashboard">
      <div className="mb-30 flex flex-col items-center justify-between gap-10 sm:flex-row sm:gap-30">
        <H2 className="font-bold">{t("email.dashboard.titles.main")}</H2>
        <div className="flex flex-col items-center gap-20 sm:flex-row">
          {getDisplayableDateRange()}
          <div className="w-[300px]">
            <Select
              label={t("email.dashboard.timePeriod.label")}
              name="time-period"
              options={timePeriodOptions}
              value={selectedTimePeriod}
              onChange={handleChangeTimePeriod}
              closeMenuOnSelect
            />
          </div>
        </div>
      </div>

      {renderDashboard()}
    </div>
  );
};

export default Dashboard;
